import { memo } from 'react'
import { InlineEdit } from './InlineEdit'
import { fileName } from '@/shared/api/generated'
import type { S3Object } from '@/shared/api/generated'

function formatSize(bytes: number): string {
  if (bytes === 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const val = bytes / Math.pow(1024, i)
  return `${i === 0 ? val : val.toFixed(1)} ${units[i]}`
}

function formatDate(iso: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  const now = Date.now()
  if (now - d.getTime() < 86400000) {
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  }
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

interface FileListRowProps {
  obj: S3Object
  index: number
  checked: boolean
  selected: boolean
  renaming: boolean
  draggable: boolean
  onCheck: (index: number) => void
  onClick: (index: number) => void
  onDoubleClick: (index: number) => void
  onContextMenu: (e: React.MouseEvent, index: number) => void
  onDownload: (index: number) => void
  onDelete?: () => void
  onRename: (index: number, newName: string) => void
  onRenameCancel: () => void
  canDownload: boolean
  canDelete: boolean
  onDragStart: (e: React.DragEvent, index: number) => void
  registryEnabled?: boolean
  iconOverride?: string
}

export const FileListRow = memo(function FileListRow({ obj, index, checked, selected, renaming, draggable, onCheck, onClick, onDoubleClick, onContextMenu, onDownload, onRename, onRenameCancel, canDownload, onDragStart, registryEnabled, iconOverride }: FileListRowProps) {
  const name = fileName(obj)
  const icon = iconOverride ?? (obj.is_dir ? '📁' : '📄')

  return (
    <tr
      className={selected ? 'selected' : undefined}
      onClick={() => onClick(index)}
      onDoubleClick={() => onDoubleClick(index)}
      onContextMenu={(e) => onContextMenu(e, index)}
      draggable={draggable && !renaming}
      onDragStart={(e) => onDragStart(e, index)}
      data-testid="file-row"
    >
      <td className="col-check">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => { e.stopPropagation(); onCheck(index) }}
          onClick={(e) => e.stopPropagation()}
        />
      </td>
      <td className="col-badge">
        {registryEnabled && <span className="registry-badge" title="Has registry">📋</span>}
      </td>
      <td className="col-name">
        <span className="icon">{icon}</span>
        {renaming ? (
          <InlineEdit initialValue={name} onSave={(newName) => onRename(index, newName)} onCancel={onRenameCancel} />
        ) : (
          <span className={obj.is_dir ? 'folder-name' : undefined}>{name}</span>
        )}
      </td>
      <td className="col-size">
        {obj.is_dir ? '—' : formatSize(obj.size)}
      </td>
      <td className="col-date">
        {formatDate(obj.last_modified)}
      </td>
      <td className="col-actions">
        {!obj.is_dir && canDownload && (
          <button
            onClick={(e) => { e.stopPropagation(); onDownload(index) }}
            title="Download"
          >⬇</button>
        )}
      </td>
    </tr>
  )
})
